<template>
    <div class="dropdown-wrapper" ref="dropdown">
        <trigger-field
                :icon="icon"
                :label="label"
                :is-open="isOpen"
                @toggle="toggleDropdown"
                :field-height="fieldHeight"
                :selection="displaySelection"
                :item-text="itemText"
                :item-value="itemValue"
                :items="items"
                :type="type"
                :loading="loading"
                @remove="emitRemove"
        />
        <div v-if="isOpen" class="dropdown-menu" :class="position">
            <div v-if="type === 'daterange'">
                <date-range-menu
                        ref="dateMenu"
                        :init-dates="selectedDates"
                        @select="updateDate"
                        :format-emit="formatEmit"
                        :init-radio-state="dateMenuRadioState"
                />
            </div>

            <div v-else>
                <div class="search-wrapper" v-if="showSearch">
                    <div class="input-with-icon">
                        <input type="text" v-model="search" :placeholder="`${t('Search')}...`"/>
                        <i class="fal fa-search search-icon"/>
                    </div>
                </div>

                <single-select-menu
                        v-if="type === 'single'"
                        :filtered-items="filteredItems"
                        :item-text="itemText"
                        :item-value="itemValue"
                        :selected-item="selectedItem"
                        @select="selectSingle"
                />
                <multi-select-menu
                        v-if="type === 'multiple'"
                        :filtered-items="filteredItems"
                        :item-text="itemText"
                        :item-value="itemValue"
                        :selected-items="selectedItems"
                        @update="updateMultiple"
                />
            </div>
        </div>
    </div>
</template>

<script>
import TriggerField from "./trigger/trigger-field.vue";
import SingleSelectMenu from "@/components/dropdown/menu/single-select-menu.vue";
import MultiSelectMenu from "@/components/dropdown/menu/multi-select-menu.vue";
import DateRangeMenu from "@/components/dropdown/menu/date-range-menu.vue";

export default {
    components: { DateRangeMenu, MultiSelectMenu, SingleSelectMenu, TriggerField },
    props: {
        icon: { type: String },
        label: { type: String },
        type: {
            type: String,
            required: true,
            validator: (value) => ["single", "multiple", "daterange"].includes(value),
        },
        position: {
            type: String,
            default: "bottom",
            validator: (v) => ["top", "bottom"].includes(v),
        },
        items: { type: Array },
        itemText: { type: String, default: "text" },
        itemValue: { type: String, default: "value" },
        value: { type: [String, Number, Array, Object], default: null },
        formatEmit: { type: Boolean },
        showSearch: { type: Boolean, default: true },
        fieldHeight: { type: Number, default: 44 },
        loading: { type: Boolean, default: false },
    },
    data() {
        return {
            isOpen: false,
            search: "",
            debouncedSearch: "",
            debounceTimer: null,
            selectedItem: null,
            selectedItems: [],
            selectedDates: null,
            dateMenuRadioState: null,
        };
    },
    computed: {
        filteredItems() {
            if (!this.items) return [];
            if (!this.debouncedSearch) return this.items.slice(0, 500);
            const searchLower = this.debouncedSearch.toLowerCase();
            return this.items.filter((item) => {
                return item[this.itemText] && item[this.itemText].toLowerCase().includes(searchLower);
            }).slice(0, 500);
        },
        displaySelection() {
            if (this.type === "single") {
                return this.selectedItem
            }
            if (this.type === "multiple") {
                return this.selectedItems
            }
            if (this.type === "daterange") {
                return this.selectedDates;
            }
            return null;
        },
    },
    watch: {
        value: {
            handler(newVal) {
                if (this.type === "multiple") {
                    this.selectedItems = newVal || [];
                } else if (this.type === "single") {
                    this.selectedItem = newVal;
                } else if (this.type === "daterange") {
                    this.selectedDates = newVal;
                }
            },
            immediate: true,
        },
        search(newVal) {
            if (this.debounceTimer) {
                clearTimeout(this.debounceTimer);
            }
            this.debounceTimer = setTimeout(() => {
                this.debouncedSearch = newVal;
            }, 300);
        },
    },
    methods: {
        toggleDropdown() {
            if (this.loading) return;
            this.isOpen = !this.isOpen;
            if (this.isOpen) {
                this.search = "";
                this.debouncedSearch = "";
            }
        },
        handleClickOutside(event) {
            if (!this.$refs.dropdown) return;
            const isClickOutside = !this.$refs.dropdown.contains(event.target);
            if (!isClickOutside) return;
            if (this.type === "daterange" && this.$refs.dateMenu && this.$refs.dateMenu.dateDialog) return;
            this.isOpen = false;
        },
        selectSingle(value) {
            this.selectedItem = value;
            this.$emit("select", this.selectedItem);
        },
        updateMultiple(value) {
            this.selectedItems = value;
            this.$emit("select", this.selectedItems);
        },
        updateDate(value) {
            this.selectedItems = value;
            this.dateMenuRadioState = this.$refs.dateMenu.radioState;
            this.$emit("select", this.selectedItems);
        },
        emitRemove(value) {
            this.$emit("remove", value);
        }
    },
    mounted() {
        document.addEventListener("click", this.handleClickOutside);
    },
    beforeDestroy() {
        document.removeEventListener("click", this.handleClickOutside);
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
        }
    },
};
</script>

<style scoped>
.dropdown-wrapper {
    position: relative;
    display: inline-block;
    width: 100%;
}

.dropdown-menu {
    position: absolute;
    left: 0;
    background-color: white;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    z-index: 10;
    padding: 8px 12px;
    min-width: 100%;
    width: max-content;
}

.checkbox-list label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    cursor: pointer;
}

.search-wrapper {
    margin-bottom: 8px;
}

.input-with-icon {
    position: relative;
}

.input-with-icon input {
    width: 100%;
    padding: 11px 36px 11px 12px;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    font-size: 14px;
    height: 40px;
    transition: border-color 0.2s;
}

.input-with-icon input:focus {
    outline: none;
    border-color: #b0b0b0;
}

.search-icon {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #999;
    pointer-events: none;
    font-size: 16px;
}

.dropdown-menu.bottom {
    top: 100%;
}

.dropdown-menu.top {
    bottom: 100%;
}
</style>
